import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { parseApiErrorMessage } from "./utils";
import { MSG } from "./constants/userMessages";
import "./i18n";
import { PermissionProvider } from './context/PermissionContext';
import QueryProvider from './providers/QueryProvider';
import { setupAxiosInterceptors } from './api/setupAxiosInterceptors';
import { CurrentVesselProvider } from './context/CurrentVesselContext';

setupAxiosInterceptors();

/**
 * Suppress the benign "ResizeObserver loop ..." browser notification.
 * It is a spec-defined notification (not a real exception) but CRA's
 * react-error-overlay surfaces it as an uncaught error in dev mode.
 * Silencing it only stops the dev overlay; real errors still propagate.
 * Refs: https://github.com/WICG/resize-observer/issues/38
 */
const isResizeObserverNoise = (msg) =>
  typeof msg === "string" && /ResizeObserver loop/i.test(msg);

const swallowResizeObserverEvent = (e) => {
  const msg = e?.message || e?.reason?.message || "";
  if (isResizeObserverNoise(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return true;
  }
  console.error("FATAL REACT ERROR:", e?.error || e?.reason || e);
  return false;
};

window.addEventListener("error", swallowResizeObserverEvent, true);
window.addEventListener("unhandledrejection", swallowResizeObserverEvent, true);

/**
 * ✅ Convert uncaught axios rejections into a toast instead of letting CRA's
 * dev runtime-overlay swallow the page. ResizeObserver noise is still
 * handled by swallowResizeObserverEvent above. Non-axios errors pass through
 * so real bugs remain visible in the console.
 */
const isAxiosError = (err) =>
  err && (err.isAxiosError === true || err.response || err.request);

window.addEventListener(
  "unhandledrejection",
  (e) => {
    if (!isAxiosError(e?.reason)) return;
    const status = e?.reason?.response?.status;
    const message = parseApiErrorMessage(
      e?.reason?.response?.data,
      e?.reason?.message || MSG.requestFailed
    );
    if (status && status >= 400 && status < 500) {
      toast.error(message);
    } else {
      toast.error(message || MSG.network);
    }
    e.preventDefault();
    e.stopImmediatePropagation();
  },
  true
);

const nativeConsoleError = window.console.error.bind(window.console);
window.console.error = (...args) => {
  if (args[0] && isResizeObserverNoise(String(args[0]))) return;
  nativeConsoleError(...args);
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ToastContainer />
    <BrowserRouter key="synchronous-app-router" useTransitions={false}>
      <QueryProvider>
        <PermissionProvider>
          <CurrentVesselProvider>
            <App />
          </CurrentVesselProvider>
        </PermissionProvider>
      </QueryProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Production vessel workstations keep the app shell and loaded static assets
// available during connectivity loss. Operational writes remain in the
// IndexedDB queue and are synchronized by the existing sync service.
if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").catch(() => {
      // The online application remains fully usable when registration is blocked.
    });
  });
}
